import { FC, useState, useEffect } from 'react';
import { useProfile } from '@/hooks/useProfile';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  DollarSign, 
  Users, 
  ChartLine, 
  Percent, 
  Copy, 
  CheckCircle2, 
  AlertCircle,
  Loader2
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

const PartnerDashboard: FC = () => {
  const { userProfile } = useProfile();
  const [paypalEmail, setPaypalEmail] = useState('');
  const [isEditingPaypal, setIsEditingPaypal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalEarnings: 0,
    activeReferrals: 0,
    conversionRate: 0,
    commissionRate: 50,
    pendingPayment: 0,
    nextPaymentDate: null
  });

  // Cargar datos del socio y estadísticas actualizadas
  useEffect(() => {
    const loadPartnerData = async () => {
      if (!userProfile?.id) return;

      try {
        console.log('Cargando datos del socio para:', userProfile.id);
        
        const { data: partnerData, error: fetchError } = await supabase
          .from('affiliates')
          .select(`
            *,
            referrals:referral_tracking(
              count,
              commission_amount,
              status
            )
          `)
          .eq('user_id', userProfile.id)
          .single();

        if (!partnerData && (!fetchError || fetchError.code === 'PGRST116')) {
          console.log('Creando nuevo registro de socio');
          const { data: newPartner, error: createError } = await supabase
            .from('affiliates')
            .insert([
              {
                user_id: userProfile.id,
                is_active: true,
                joined_at: new Date().toISOString(),
                total_earnings: 0,
                pending_payment: 0
              }
            ])
            .select()
            .single();

          if (createError) {
            console.error('Error al crear socio:', createError);
            throw createError;
          }

          setPaypalEmail(newPartner?.paypal_email || '');
          setStats({
            totalEarnings: 0,
            activeReferrals: 0,
            conversionRate: 0,
            commissionRate: 50,
            pendingPayment: 0,
            nextPaymentDate: null
          });
        } else if (partnerData) {
          setPaypalEmail(partnerData.paypal_email || '');
          
          // Calcular estadísticas reales
          const totalReferrals = partnerData.referrals?.length || 0;
          const paidReferrals = partnerData.referrals?.filter(r => r.status === 'paid').length || 0;
          const totalCommissions = partnerData.referrals?.reduce((sum, ref) => sum + (ref.commission_amount || 0), 0) || 0;
          const pendingCommissions = partnerData.referrals?.reduce((sum, ref) => 
            ref.status === 'paid' && !ref.commission_paid ? sum + (ref.commission_amount || 0) : sum, 0) || 0;

          setStats({
            totalEarnings: totalCommissions,
            activeReferrals: paidReferrals,
            conversionRate: totalReferrals > 0 ? Math.round((paidReferrals / totalReferrals) * 100) : 0,
            commissionRate: 50,
            pendingPayment: pendingCommissions,
            nextPaymentDate: pendingCommissions >= 50 ? 
              new Date(new Date().setDate(1)).toISOString() : null
          });
        }
      } catch (error) {
        console.error('Error completo:', error);
        toast.error('Error al cargar los datos del socio. Por favor, intenta de nuevo más tarde.');
      } finally {
        setIsLoading(false);
      }
    };

    loadPartnerData();
  }, [userProfile?.id]);

  const handlePaypalUpdate = async () => {
    if (!paypalEmail || !paypalEmail.includes('@')) {
      toast.error('Por favor, ingresa un correo de PayPal válido');
      return;
    }

    try {
      const { error } = await supabase
        .from('affiliates')
        .update({ paypal_email: paypalEmail })
        .eq('user_id', userProfile?.id);

      if (error) throw error;

      toast.success('Email de PayPal actualizado correctamente');
      setIsEditingPaypal(false);
    } catch (error) {
      console.error('Error updating PayPal email:', error);
      toast.error('Error al actualizar el email de PayPal');
    }
  };

  const copyReferralLink = async () => {
    try {
      const link = `https://financieramente.kids?ref=${userProfile?.id}`;
      await navigator.clipboard.writeText(link);
      toast.success('Link copiado al portapapeles');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error('Error al copiar el link. Por favor, cópialo manualmente.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Panel de Socio</h1>
        {!paypalEmail && (
          <div className="flex items-center gap-2 text-amber-600">
            <AlertCircle className="h-5 w-5" />
            <span className="text-sm">Configura tu email de PayPal para recibir pagos</span>
          </div>
        )}
      </div>

      <div className="grid gap-6 mb-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        <Card className="p-4">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-blue-100 rounded-full">
              <DollarSign className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Ganancias Totales</p>
              <p className="text-2xl font-bold">${stats.totalEarnings.toFixed(2)}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-green-100 rounded-full">
              <Users className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Referidos Activos</p>
              <p className="text-2xl font-bold">{stats.activeReferrals}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-purple-100 rounded-full">
              <ChartLine className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Tasa de Conversión</p>
              <p className="text-2xl font-bold">{stats.conversionRate}%</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-orange-100 rounded-full">
              <Percent className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Comisión</p>
              <p className="text-2xl font-bold">{stats.commissionRate}%</p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Tu Link de Referido</h2>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm text-gray-600 break-all">
                https://financieramente.kids?ref={userProfile?.id}
              </p>
              <Button 
                variant="outline" 
                size="sm"
                onClick={copyReferralLink}
                className="shrink-0"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copiar
              </Button>
            </div>
          </div>
          <p className="mt-2 text-sm text-gray-500">
            Comparte este link con tus referidos para ganar comisiones por sus suscripciones
          </p>
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Configuración de Pagos</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email de PayPal
              </label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={paypalEmail}
                  onChange={(e) => setPaypalEmail(e.target.value)}
                  placeholder="tu.correo@ejemplo.com"
                  disabled={!isEditingPaypal}
                  className="flex-1"
                />
                <Button
                  variant={isEditingPaypal ? "default" : "outline"}
                  onClick={() => {
                    if (isEditingPaypal) {
                      handlePaypalUpdate();
                    } else {
                      setIsEditingPaypal(true);
                    }
                  }}
                  className="whitespace-nowrap"
                >
                  {isEditingPaypal ? 'Guardar' : 'Editar'}
                </Button>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Próximo Pago</h3>
              <div className="space-y-1">
                <p className="text-sm text-gray-600">
                  Monto pendiente: ${stats.pendingPayment.toFixed(2)}
                </p>
                <p className="text-sm text-gray-600">
                  {stats.pendingPayment >= 50 ? (
                    <>Fecha estimada: {new Date(stats.nextPaymentDate!).toLocaleDateString()}</>
                  ) : (
                    <>Mínimo para cobrar: $50.00</>
                  )}
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <div className="mt-8 bg-blue-50 border border-blue-100 rounded-lg p-4">
        <h3 className="font-medium text-blue-800 mb-2">Información Importante</h3>
        <ul className="list-disc list-inside text-sm text-blue-700 space-y-1">
          <li>Las comisiones se pagan mensualmente vía PayPal</li>
          <li>El monto mínimo para cobrar es de $50</li>
          <li>Las comisiones se generan por suscripciones premium activas</li>
          <li>Mantén actualizado tu email de PayPal para recibir los pagos</li>
        </ul>
      </div>
    </div>
  );
};

export default PartnerDashboard;
